import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { TokenService } from './token.service';

const assessment = environment.api + 'assessment';
const external = environment.api + 'external';
const cases = environment.api + 'case';

const apiRoutes = {
	examinee: '/examinee',
	verifyTwoFA: '/verify-2fa',
	send_verify_two_FA: '/send-verify-2fa',
	examinee_start: '/examinee/start',
	examinee_answer: '/examinee/answer',
	examinee_send_to_merge: '/examinee/sent-to-merge',
	examinee_get_upload_url: '/examinee/get-upload-url',
}

type AssessmentContext = 'internal' | 'external' | 'case';

@Injectable({ providedIn: 'root' })
export class ExamineeService {
	private headers!: HttpHeaders;
	private examineeSubject = new BehaviorSubject<any>(null);
	private questionnaireSubject = new BehaviorSubject<any>(null);
	token: any = null;

	constructor(private http: HttpClient, private toastr: ToastrService, public jwtHelper: JwtHelperService, private router: Router, private tokenService: TokenService) { }

	private _getApiBaseRoute(): string {
    const context = this.getAssessmentContext();
    if (context === 'external') {
      return external;
    }
    if (context === 'case') {
      return cases;
    }
    return assessment; // Default to internal
  }

	private getAssessmentContext(): AssessmentContext {
    const payload = this.tokenService.getDecodedToken(this.token);
    
    if (payload?.external === true) {
      return 'external';
    }
    if (payload?.case === true) {
      return 'case';
    }
    return 'internal';
  }

	fetchExamineeDetails(auth?: boolean) {
		this.getExamineeDetails().subscribe({
			next: (response: any) => {
				this.examineeSubject.next(response.examinee);
				this.questionnaireSubject.next(response.questionnaire);
				if (!auth) {
					this.router.navigate(['assessment']);
				}
			},
			error: (err) => {
				this.logout();
				this.toastErrorMessage(err, 'User Error')
			}
		});
	}

	getContext() {
		return this.getAssessmentContext();
	}

	getExamineeDetails() {
		this.headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.token}` });
		const api_route = this._getApiBaseRoute() + apiRoutes.examinee;
    return this.http.get<any>(api_route, { headers: this.headers });
  }
	
	verifyTwoFA(code: string) {
		const api_route = this._getApiBaseRoute() + apiRoutes.verifyTwoFA;
		return this.http.post<any>(api_route, { code }, { headers: this.headers });
	}
	
	sendVerifyTwoFA() {
		const api_route = this._getApiBaseRoute() + apiRoutes.send_verify_two_FA;
		return this.http.get<any>(api_route, { headers: this.headers });
	}
	
	updateExamineeStart() {
		const api_route = this._getApiBaseRoute() + apiRoutes.examinee_start;
		return this.http.patch<any>(api_route, {}, { headers: this.headers });
	}
	
	saveAnswer(data: any) {
		const api_route = this._getApiBaseRoute() + apiRoutes.examinee_answer;
		return this.http.post<any>(api_route, data, { headers: this.headers });
	}
	
	mergeChunks(data: any) {
		const api_route = this._getApiBaseRoute() + apiRoutes.examinee_send_to_merge;
		return this.http.post<any>(api_route, data, { headers: this.headers });
	}
	
	async getUploadURL(fileName: string, contentType: string) {
		const api_route = this._getApiBaseRoute() + apiRoutes.examinee_get_upload_url;
		const response$ = this.http.post<any>(api_route, { fileName, contentType }, { headers: this.headers });
		const response = await firstValueFrom(response$);
    return response;
	}

	getChunkUploadPath(chunkIndex: number): string {
		const context = this.getAssessmentContext();
		const examinee = this.examineeValue;

		if (!examinee) {
			console.error("getChunkUploadPath called before examinee was loaded.");
			return '';
		}

		const chunkName = `chunk_${chunkIndex.toString().padStart(3, '0')}.webm`;
		let basePath: string;

		if (context === 'external') {
			basePath = `external-chunks/examinee-${examinee.id}`;
		} else if (context === 'case') {
			if (!examinee.project_id || !examinee.case_id) {
				console.error("Case examinee is missing project_id or case_id. Cannot build path.");
				return '';
			}
			basePath = `project_${examinee.project_id}/case_${examinee.case_id}/chunks/examinee-${examinee.id}`;
		} else {
			basePath = `examinee-chunks/examinee-${examinee.id}`;
		}

		return `${basePath}/${chunkName}`;
	}

	logout() {
		this.router.navigate(['/not-found']);
		// localStorage.removeItem('token');
		// this.token = null;
		// this.examineeSubject.next(null);
		// this.questionnaireSubject.next(null);
	}

	setToken(token: any) {
		this.token = token;
		localStorage.setItem('token', this.token);
		this.fetchExamineeDetails();
	}

	setAuthToken(token: any) {
		this.token = token;
		this.fetchExamineeDetails(true);
	}

	toastErrorMessage(detail: string | undefined, title: string | undefined) {
    this.toastr.error(detail, title);
  }

  toastSuccessMessage(detail: string | undefined, title: string | undefined) {
    this.toastr.success(detail, title);
  }

	setExamineeSubject(examinee: any) {
		this.examineeSubject.next(examinee);
	}

	get examineeValue(): any {
		return this.examineeSubject.value;
	}

	setQuestionnaireSubject(questionnaire: any) {
		this.questionnaireSubject.next(questionnaire);
	}

	get questionnaireValue(): any {
		return this.questionnaireSubject.value;
	}
}